/**
 * Media Session API helpers — lock screen / headset / OS media keys.
 * Safe no-ops when the browser does not support navigator.mediaSession.
 */

const SURAH_NAMES = [
  '',
  'Al-Fatihah',
  'Al-Baqarah',
  "Ali 'Imran",
  'An-Nisa',
  "Al-Ma'idah",
  "Al-An'am",
  "Al-A'raf",
  'Al-Anfal',
  'At-Tawbah',
  'Yunus',
  'Hud',
  'Yusuf',
  "Ar-Ra'd",
  'Ibrahim',
  'Al-Hijr',
  'An-Nahl',
  'Al-Isra',
  'Al-Kahf',
  'Maryam',
  'Ta-Ha',
  'Al-Anbiya',
  'Al-Hajj',
  "Al-Mu'minun",
  'An-Nur',
  'Al-Furqan',
  "Ash-Shu'ara",
  'An-Naml',
  'Al-Qasas',
  'Al-Ankabut',
  'Ar-Rum',
  'Luqman',
  'As-Sajdah',
  'Al-Ahzab',
  'Saba',
  'Fatir',
  'Ya-Sin',
  'As-Saffat',
  'Sad',
  'Az-Zumar',
  'Ghafir',
  'Fussilat',
  'Ash-Shura',
  'Az-Zukhruf',
  'Ad-Dukhan',
  'Al-Jathiyah',
  'Al-Ahqaf',
  'Muhammad',
  'Al-Fath',
  'Al-Hujurat',
  'Qaf',
  'Adh-Dhariyat',
  'At-Tur',
  'An-Najm',
  'Al-Qamar',
  'Ar-Rahman',
  'Al-Waqi\'ah',
  'Al-Hadid',
  'Al-Mujadila',
  'Al-Hashr',
  'Al-Mumtahanah',
  'As-Saff',
  "Al-Jumu'ah",
  'Al-Munafiqun',
  'At-Taghabun',
  'At-Talaq',
  'At-Tahrim',
  'Al-Mulk',
  'Al-Qalam',
  'Al-Haqqah',
  "Al-Ma'arij",
  'Nuh',
  'Al-Jinn',
  'Al-Muzzammil',
  'Al-Muddaththir',
  'Al-Qiyamah',
  'Al-Insan',
  'Al-Mursalat',
  'An-Naba',
  "An-Nazi'at",
  'Abasa',
  'At-Takwir',
  'Al-Infitar',
  'Al-Mutaffifin',
  'Al-Inshiqaq',
  'Al-Buruj',
  'At-Tariq',
  "Al-A'la",
  'Al-Ghashiyah',
  'Al-Fajr',
  'Al-Balad',
  'Ash-Shams',
  'Al-Layl',
  'Ad-Duha',
  'Ash-Sharh',
  'At-Tin',
  'Al-Alaq',
  'Al-Qadr',
  'Al-Bayyinah',
  'Az-Zalzalah',
  'Al-Adiyat',
  "Al-Qari'ah",
  'At-Takathur',
  'Al-Asr',
  'Al-Humazah',
  'Al-Fil',
  'Quraysh',
  "Al-Ma'un",
  'Al-Kawthar',
  'Al-Kafirun',
  'An-Nasr',
  'Al-Masad',
  'Al-Ikhlas',
  'Al-Falaq',
  'An-Nas',
];

function hasMediaSession() {
  return typeof navigator !== 'undefined' && !!navigator.mediaSession;
}

function artworkList() {
  if (typeof window === 'undefined') return [];
  const origin = window.location.origin;
  return [
    { src: `${origin}/quran_logo192.png`, sizes: '192x192', type: 'image/png' },
    { src: `${origin}/quran_logo512.png`, sizes: '512x512', type: 'image/png' },
  ];
}

export function surahDisplayName(surahId) {
  const id = Number(surahId);
  if (!id || id < 1 || id > 114) return `Surah ${surahId || ''}`;
  return SURAH_NAMES[id] || `Surah ${id}`;
}

/**
 * @param {{ surahId?: number|null, ayahKey?: string|null, mode?: string|null, artist?: string }} info
 */
export function updateMediaSessionMetadata(info = {}) {
  if (!hasMediaSession() || typeof MediaMetadata === 'undefined') return;

  const surahId =
    info.surahId ||
    (info.ayahKey ? Number(String(info.ayahKey).split(':')[0]) : null);
  const ayahNum = info.ayahKey
    ? Number(String(info.ayahKey).split(':')[1])
    : null;
  const name = surahDisplayName(surahId);
  const title =
    ayahNum && Number.isFinite(ayahNum)
      ? `${name} | Ayah ${ayahNum}`
      : name || 'Quran';

  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: info.artist || 'Quran Recitation',
      album: info.mode === 'ayah' ? 'Ayah playback' : 'Surah playback',
      artwork: artworkList(),
    });
  } catch {
    /* ignore unsupported fields */
  }
}

/**
 * @param {'none'|'paused'|'playing'} state
 */
export function setMediaSessionPlaybackState(state) {
  if (!hasMediaSession()) return;
  try {
    navigator.mediaSession.playbackState = state;
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ duration: number, position: number, playbackRate?: number }} pos
 */
export function setMediaSessionPositionState(pos) {
  if (!hasMediaSession() || typeof navigator.mediaSession.setPositionState !== 'function') {
    return;
  }
  const duration = Number(pos.duration);
  const position = Number(pos.position);
  if (!Number.isFinite(duration) || duration <= 0) return;
  const clamped = Math.max(0, Math.min(position || 0, duration));
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: pos.playbackRate || 1,
      position: clamped,
    });
  } catch {
    /* Safari can throw if values are inconsistent */
  }
}

/**
 * @param {Record<string, Function|null>} handlers
 *   Keys: play, pause, stop, seekbackward, seekforward, seekto, previoustrack, nexttrack
 */
export function bindMediaSessionHandlers(handlers) {
  if (!hasMediaSession()) return () => {};

  const keys = Object.keys(handlers);
  keys.forEach((action) => {
    try {
      navigator.mediaSession.setActionHandler(action, handlers[action] || null);
    } catch {
      /* action not supported on this browser */
    }
  });

  return () => {
    keys.forEach((action) => {
      try {
        navigator.mediaSession.setActionHandler(action, null);
      } catch {
        /* ignore */
      }
    });
  };
}

export function clearMediaSession() {
  if (!hasMediaSession()) return;
  try {
    navigator.mediaSession.metadata = null;
    navigator.mediaSession.playbackState = 'none';
  } catch {
    /* ignore */
  }
  if (typeof navigator.mediaSession.setPositionState === 'function') {
    try {
      navigator.mediaSession.setPositionState(null);
    } catch {
      /* ignore */
    }
  }
}
